<template>
  <div class="operations-summary">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fas fa-calendar me-2 text-primary"></i>
          {{ $t("operationsSummary.title") }}
          
        </h5>
      </div>
      <div class="card-body">
        <div v-if="summaryMessages.length > 0">
          <div
            v-for="(message, index) in summaryMessages"
            :key="index"
          >
            <div class="d-flex align-items-start">
              <div class="message-icon me-3">
                <i :class="message.icon" :style="{ color: message.color }"></i>
              </div>
              <div class="message-content">
                <div class="message-text" v-html="message.text"></div>
                <small class="text-muted">{{ message.dateInfo }}</small>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted text-center">
          <i class="fas fa-calendar-check mb-2 d-block"></i>
          {{ $t("operationsSummary.noOperations") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { buildDashboardEvents } from "@/utils/dashboardEventTypes";

export default {
  name: "OperationsSummary",
  props: {
    modelData: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Campos de fecha de calendario por modelo, tal como los ofrece el engranaje:
     * `[{ modelName, fields: [{ key, label }] }]`.
     *
     * Reemplaza la lista de campos que este componente tenía escrita a mano y que no
     * coincidía con la del engranaje: en Duntower las fechas de cut off son `cod` y
     * `cop`, pero acá se buscaban como `cut_off_documents` / `cut_off_physical`, así
     * que el resumen nunca generaba los mensajes de cut off. Ahora el calendario,
     * los bloques de la derecha y este resumen miran los mismos campos.
     */
    calendarModels: {
      type: Array,
      default: () => [],
    },
    calendarPreferences: {
      type: Object,
      default: () => ({}),
    },
    /** La ETA trae solo embarques en rojo. Ver `FIELD_STATUS_RULES`. */
    applyFieldStatusRules: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allEvents() {
      return buildDashboardEvents({
        modelData: this.modelData,
        calendarModels: this.calendarModels,
        calendarPreferences: this.calendarPreferences,
        applyFieldStatusRules: this.applyFieldStatusRules,
      });
    },

    summaryMessages() {
      const messages = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      // Agrupar eventos por tipo y fecha
      const eventsByType = this.groupEventsByType();

      // Generar mensajes para cada tipo
      Object.keys(eventsByType).forEach((type) => {
        const events = eventsByType[type];
        const message = this.generateMessageForType(
          type,
          events,
          today,
          tomorrow,
          nextWeek
        );
        if (message) {
          messages.push(message);
        }
      });

      return messages.sort((a, b) => a.priority - b.priority);
    },
  },
  methods: {
    /**
     * Agrupa por el tipo de mensaje del resumen. Una fecha sin `summaryType` (un
     * campo de calendario propio de un tenant, sin mensaje definido) no se agrupa:
     * el resumen solo habla de los conceptos para los que hay texto.
     */
    groupEventsByType() {
      const groups = {};
      this.allEvents.forEach((event) => {
        if (!event.summaryType) return;
        if (!groups[event.summaryType]) {
          groups[event.summaryType] = [];
        }
        groups[event.summaryType].push(event);
      });
      return groups;
    },

    generateMessageForType(type, events, today, tomorrow, nextWeek) {
      const todayEvents = events.filter((e) => this.isSameDay(e.date, today));
      const tomorrowEvents = events.filter((e) =>
        this.isSameDay(e.date, tomorrow)
      );
      const thisWeekEvents = events.filter(
        (e) => e.date > tomorrow && e.date <= nextWeek
      );

      if (
        todayEvents.length === 0 &&
        tomorrowEvents.length === 0 &&
        thisWeekEvents.length === 0
      ) {
        return null;
      }

      let message = "";
      let icon = "fas fa-calendar";
      let color = "#6c757d";
      let priority = 3;

      // Generar mensaje según el tipo
      switch (type) {
        case "eta":
          message = this.generateETAMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-ship";
          color = "#dc3545";
          priority = 1;
          break;
        case "etd":
          message = this.generateETDMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-anchor";
          color = "#fd7e14";
          priority = 2;
          break;
        case "loading":
          message = this.generateLoadingMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-boxes";
          color = "#0d6efd";
          priority = 2;
          break;
        case "gate_out":
          message = this.generateGateOutMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-truck";
          color = "#198754";
          priority = 2;
          break;
        case "payment":
          message = this.generatePaymentMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-dollar-sign";
          color = "#dc3545";
          priority = 1;
          break;
        case "cut_off_docs":
        case "cut_off_physical":
          message = this.generateCutOffMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents,
            type
          );
          icon = "fas fa-clock";
          color = "#dc3545";
          priority = 1;
          break;
        case "date_released":
          message = this.generateDateReleasedMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-file-alt";
          color = "#6f42c1";
          priority = 3;
          break;
        case "import_permit":
          message = this.generateImportPermitMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-passport";
          color = "#fd7e14";
          priority = 2;
          break;
        case "payment_pending":
          message = this.generatePaymentPendingMessage(
            todayEvents,
            tomorrowEvents,
            thisWeekEvents
          );
          icon = "fas fa-exclamation-triangle";
          color = "#dc3545";
          priority = 1;
          break;
        default:
          return null;
      }

      return {
        text: message,
        icon,
        color,
        priority,
        dateInfo: this.getDateInfo(todayEvents, tomorrowEvents, thisWeekEvents),
      };
    },

    generateMessage(type, today, tomorrow, thisWeek) {
      const messages = this.$t(`operationsSummary.messages.${type}`);
      const words = this.$t("operationsSummary.words");

      if (today.length > 0) {
        return this.formatMessage(messages.today, today.length, words);
      }
      if (tomorrow.length > 0) {
        return this.formatMessage(messages.tomorrow, tomorrow.length, words);
      }
      if (thisWeek.length > 0) {
        return this.formatMessage(messages.thisWeek, thisWeek.length, words);
      }
      return null;
    },

    formatMessage(template, count, words) {
      const isPlural = count > 1;
      let message = template;

      // Replace placeholders based on the message type
      if (template.includes("{vessel}")) {
        message = message.replace(
          "{vessel}",
          isPlural ? words.vessel.plural : words.vessel.single
        );
        message = message.replace(
          "{verb}",
          isPlural ? words.arrives.plural : words.arrives.single
        );
      } else if (template.includes("{container}")) {
        message = message.replace(
          "{container}",
          isPlural ? words.container.plural : words.container.single
        );
        if (template.includes("se carga")) {
          message = message.replace(
            "{verb}",
            isPlural ? words.loads.plural : words.loads.single
          );
        } else {
          message = message.replace(
            "{verb}",
            isPlural ? words.goesOut.plural : words.goesOut.single
          );
        }
      } else if (template.includes("{payment}")) {
        message = message.replace(
          "{payment}",
          isPlural ? words.payment.plural : words.payment.single
        );
        message = message.replace(
          "{verb}",
          isPlural ? words.expires.plural : words.expires.single
        );
      } else if (template.includes("{operation}")) {
        message = message.replace(
          "{operation}",
          isPlural ? words.operation.plural : words.operation.single
        );
        message = message.replace(
          "{verb}",
          isPlural ? words.has.plural : words.has.single
        );
      } else if (template.includes("{document}")) {
        message = message.replace(
          "{document}",
          isPlural ? words.document.plural : words.document.single
        );
        message = message.replace(
          "{verb}",
          isPlural ? words.releases.plural : words.releases.single
        );
      } else if (template.includes("{permit}")) {
        message = message.replace(
          "{permit}",
          isPlural ? words.permit.plural : words.permit.single
        );
        message = message.replace(
          "{verb}",
          isPlural ? words.expires.plural : words.expires.single
        );
      }

      message = message.replace("{count}", count);
      return message;
    },

    generateETAMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("eta", today, tomorrow, thisWeek);
    },

    generateETDMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("etd", today, tomorrow, thisWeek);
    },

    generateLoadingMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("loading", today, tomorrow, thisWeek);
    },

    generateGateOutMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("gateOut", today, tomorrow, thisWeek);
    },

    generatePaymentMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("payment", today, tomorrow, thisWeek);
    },

    generateCutOffMessage(today, tomorrow, thisWeek, type) {
      const messageType =
        type === "cut_off_docs" ? "cutOffDocs" : "cutOffPhysical";
      return this.generateMessage(messageType, today, tomorrow, thisWeek);
    },

    generateDateReleasedMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("dateReleased", today, tomorrow, thisWeek);
    },

    generateImportPermitMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("importPermit", today, tomorrow, thisWeek);
    },

    generatePaymentPendingMessage(today, tomorrow, thisWeek) {
      return this.generateMessage("payment_pending", today, tomorrow, thisWeek);
    },

    getDateInfo(today, tomorrow, thisWeek) {
      const isSpanish = this.$i18n.locale === "es";
      const isFrench = this.$i18n.locale === "fr";
      const isPortuguese = this.$i18n.locale === "pt";
      const isUkrainian = this.$i18n.locale === "uk";

      if (today.length > 0) {
        if (isSpanish)
          return `Hoy: ${today.length} evento${today.length > 1 ? "s" : ""}`;
        if (isFrench)
          return `Aujourd'hui: ${today.length} événement${
            today.length > 1 ? "s" : ""
          }`;
        if (isPortuguese)
          return `Hoje: ${today.length} evento${today.length > 1 ? "s" : ""}`;
        if (isUkrainian)
          return `Сьогодні: ${today.length} подія${today.length > 1 ? "" : ""}`;
        return `Today: ${today.length} event${today.length > 1 ? "s" : ""}`;
      }
      if (tomorrow.length > 0) {
        if (isSpanish)
          return `Mañana: ${tomorrow.length} evento${
            tomorrow.length > 1 ? "s" : ""
          }`;
        if (isFrench)
          return `Demain: ${tomorrow.length} événement${
            tomorrow.length > 1 ? "s" : ""
          }`;
        if (isPortuguese)
          return `Amanhã: ${tomorrow.length} evento${
            tomorrow.length > 1 ? "s" : ""
          }`;
        if (isUkrainian)
          return `Завтра: ${tomorrow.length} подія${
            tomorrow.length > 1 ? "" : ""
          }`;
        return `Tomorrow: ${tomorrow.length} event${
          tomorrow.length > 1 ? "s" : ""
        }`;
      }
      if (thisWeek.length > 0) {
        if (isSpanish)
          return `Esta semana: ${thisWeek.length} evento${
            thisWeek.length > 1 ? "s" : ""
          }`;
        if (isFrench)
          return `Cette semaine: ${thisWeek.length} événement${
            thisWeek.length > 1 ? "s" : ""
          }`;
        if (isPortuguese)
          return `Esta semana: ${thisWeek.length} evento${
            thisWeek.length > 1 ? "s" : ""
          }`;
        if (isUkrainian)
          return `Цього тижня: ${thisWeek.length} подія${
            thisWeek.length > 1 ? "" : ""
          }`;
        return `This week: ${thisWeek.length} event${
          thisWeek.length > 1 ? "s" : ""
        }`;
      }
      return "";
    },

    isSameDay(date1, date2) {
      return date1.toDateString() === date2.toDateString();
    },

    showHelp() {
      const helpData = this.$t("operationsSummary.helpText");
      const sections = helpData.sections;

      let html = '<div class="text-start">';

      // Fechas Principales del Embarque
      html += `<h6>📅 ${sections.mainDates.title}</h6>`;
      html += `<p><strong>${sections.mainDates.eta}</strong></p>`;
      html += `<p><strong>${sections.mainDates.etd}</strong></p>`;

      // Cut Offs
      html += `<h6>⏰ ${sections.cutOffs.title}</h6>`;
      html += `<p><strong>${sections.cutOffs.documents}</strong></p>`;
      html += `<p><strong>${sections.cutOffs.physical}</strong></p>`;

      // Movimientos de Carga
      html += `<h6>📦 ${sections.cargoMovements.title}</h6>`;
      html += `<p><strong>${sections.cargoMovements.loading}</strong></p>`;
      html += `<p><strong>${sections.cargoMovements.gateOut}</strong></p>`;

      // Documentación Comercial
      html += `<h6>📋 ${sections.documentation.title}</h6>`;
      html += `<p><strong>${sections.documentation.dateReleased}</strong></p>`;

      // Permisos de Importación
      html += `<h6>🛂 ${sections.importPermits.title}</h6>`;
      html += `<p><strong>${sections.importPermits.expiration}</strong></p>`;

      // Fechas de Pago
      html += `<h6>💰 ${sections.payments.title}</h6>`;
      html += `<p><strong>${sections.payments.paymentDate}</strong></p>`;

      html += "</div>";

      Swal.fire({
        title: this.$tn("operationsSummary.help"),
        html: html,
        icon: "info",
        confirmButtonText: this.$tn("operationsSummary.close"),
        confirmButtonColor: "#3085d6",
      });
    },
  },
};
</script>

<style scoped>
.operations-summary {
  margin-top: 20px;
}

.summary-message {
  border-left: 4px solid #e9ecef;
  padding-left: 15px;
  transition: all 0.2s ease;
}

.summary-message:hover {
  border-left-color: #007bff;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 10px 15px;
  margin-left: -10px;
}

.message-icon {
  font-size: 1.2em;
  margin-top: 2px;
}

.message-content {
  flex: 1;
}

.message-text {
  font-size: 0.95em;
  line-height: 1.4;
  margin-bottom: 5px;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.7;
}

.card-header h5 {
  font-size: 1.1rem;
  font-weight: 600;
}
</style>
